import { AuthProviders, SubscriptionTypes } from './enums';
import { CreateUserSource } from './enums/createUserSource';
import { EntityTypes } from './enums/entityTypes';
import { UserStatus } from './enums/userStatus';
import { UserTypes } from './enums/userTypes';
import { Org } from './org';
import { SourcingAllySubscription } from './sourcingAllySubscription';

export interface User {
  orgId: string;
  orgName: string;
  userId: string;
  entityType: EntityTypes.user;
  userEmail: string;
  firstName: string;
  lastName: string;
  title: string;
  phone?: string;
  created: number;
  lastUpdated: number;
  cognitoSub: string;
  userStatus: UserStatus;
  userType: UserTypes;
  org?: Org;
  lastLogin?: number;
  admin? : boolean;
}
export interface SaaSUser extends User {
  preferences: SaaSPreferenceData;
}

export interface SaaSPreferenceData {
  shownSupplierRequestMatchesTutorial?: boolean;
  shownScaleupSaaSTutorial?: boolean;
  acceptedTsAndCs?: boolean;
}

export interface PlatformUser extends User {
  preferences: PlatformPreferenceData;
}

export interface PlatformPreferenceData {
  shownGrowthPlannerCTA?: boolean;
  hasCompletedSupplierIntake?: boolean;
  shownGrowthPlannerTutorial?: boolean;
  acceptedTermsAndConditions?: boolean;
  acceptedTermsAndConditionsDate?: number;
}

export interface SourcingAllyUser extends User {
  stripeCustomerId?: string;
  memberStackId?: string;
  subscription?: SourcingAllySubscription;
  preferences: SourcingAllyPreferenceData;
  acceptedTsAndCsDate?: number;
}

export interface SourcingAllyProvisionalUser {
  userId: string;
  userEmail: string;
  userType: UserTypes.member;
  entityType: EntityTypes.user;
  firstName?: string;
  lastName?: string;
  title?: string;
  memberStackId: string;
  created: number;
  lastUpdated: number;
}

export interface CreateProvisionalUserProps {
  userEmail: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  memberStackId: string;
}

export interface UpgradeProvisionalUserProps {
  userId: string;
  orgId: string;
  cognitoSub: string;
  stripeCustomerId?: string;
}
export interface SourcingAllyPreferenceData {
  shownSourcingRequestMatchesTutorial?: boolean;
  acceptedTsAndCs?: boolean;
}

export interface CreateUserProps {
  userEmail: string;
  firstName: string;
  lastName: string;
  title: string;
  orgId: string;
  orgName: string;
  cognitoSub: string;
  userType: UserTypes;
  hasCompletedSupplierIntake?: boolean;
}

export interface CreateSourcingAllyUserProps {
  userEmail: string;
  firstName: string;
  lastName: string;
  title: string;
  orgId: string;
  cognitoSub: string;
  stripeCustomerId?: string;
  memberStackId: string;
}

export interface CreateUserMessage {
  userEmail: string;
  userType: UserTypes;
  orgId: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  source: CreateUserSource;
  sourceReferenceId: string;
}

export interface CreateSourcingAllyUserMessage {
  userEmail: string;
  userType: UserTypes.member;
  orgId: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  subscriptionTier: SubscriptionTypes;
  subscriptionToken: string;
}

export interface CreateUserAPIProps {
  userEmail: string;
  firstName: string;
  lastName: string;
  title: string;
  userType: UserTypes;
}

export interface CreateUserAdminAPIProps {
  userEmail: string;
  firstName: string;
  lastName: string;
  title: string;
  userType: UserTypes;
  orgId: string;
}

//JSON Schema for CreateUserAdminAPIProps
export const CreateUserAdminAPIPropsSchema = {
  type: 'object',
  properties: {
    userEmail: { type: 'string', format: 'email' },
    firstName: { type: 'string', minLength: 2 },
    lastName: { type: 'string', minLength: 2 },
    title: { type: 'string', minLength: 2 },
    userType: { type: 'string', format: 'enum', enum: Object.values(UserTypes) },
    orgId: { type: 'string' },
  },
  required: ['userEmail', 'firstName', 'lastName', 'title', 'userType', 'orgId'],
};

export interface CurrentUserUpdateProps {
  firstName?: string;
  lastName?: string;
  title?: string;
}

export interface CurrentPlatformUserUpdateProps {
  firstName?: string;
  lastName?: string;
  title?: string;
  preferences?: PlatformPreferenceData;
}
export interface UserUpdateProps {
  firstName?: string;
  lastName?: string;
  title?: string;
  userType?: UserTypes;
  userStatus?: UserStatus;

  phone?: string;
  userEmail?: string;
}

export interface InvitedUser {
  userEmail: string;
  userType: UserTypes;
}

export interface SourcingAllyUserSignupProps {
  email: string;
  firstName?: string;
  lastName?: string;
  referrer?: string;
  redirectUrl?: string;
}

//JSON Schema for SourcingAlly User Signup
export const SourcingAllyUserSignupApiInputSchema = {
  type: 'object',
  properties: {
    email: { type: 'string', format: 'email' },
    firstName: { type: 'string', minLength: 2 },
    lastName: { type: 'string', minLength: 2 },
    referrer: { type: 'string'},
    redirectUrl: { type: 'string', format: 'url' },
  },
  required: ['email'],
};

export interface SourcingAllyUserFirstTimeLinkProps {
  email: string;
  memberStackId: string;
}

//JSOM Schema for SourcingAlly User First Time Link
export const SourcingAllyUserFirstTimeLinkPropsSchema = {
  type: 'object',
  properties: {
    email: { type: 'string', format: 'email' },
    memberStackId: { type: 'string' },
  },
  required: ['email', 'memberStackId'],
};

export interface SourcingAllyUserAuthProps {
  provider: AuthProviders;
  token: string;
  redirectUrl: string;
  createNewUser?: boolean;
}

//JSON Schema for SourcingAlly User Auth
export const SourcingAllyUserAuthApiInputSchema = {
  type: 'object',
  properties: {
    provider: { type: 'string', format: 'enum', enum: Object.values(AuthProviders) },
    token: { type: 'string' },
    redirectUrl: { type: 'string', format: 'url' },
    createNewUser: { type: 'boolean'},
  },
  required: ['provider', 'token', 'redirectUrl'],
};


export interface UserMetrics {
  mostRecentLogins: User[];
  todayActiveUsers: User[];
}